import { createFileRoute, Link } from "@tanstack/react-router";
import { CheckCircle2, ArrowRight, Mail, Phone, Clock, Sparkles, Home, ClipboardList } from "lucide-react";

export const Route = createFileRoute("/tack")({
  head: () => ({
    meta: [
      { title: "Tack för din förfrågan — Event Sverige" },
      { name: "description", content: "Din förfrågan är sparad. Vårt team granskar upplägget och återkommer med pris och tillgänglighet." },
      { name: "robots", content: "noindex" },
      { property: "og:title", content: "Tack för din förfrågan — Event Sverige" },
      { property: "og:description", content: "Din förfrågan är sparad och granskas av vårt team." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
      { name: "twitter:description", content: "Din förfrågan är sparad och granskas av vårt team." },
    ],
  }),
  component: TackPage,
});

function TackPage() {
  return (
    <div>
      {/* HERO */}
      <section className="relative overflow-hidden bg-primary text-primary-foreground">
        <div className="absolute inset-0 bg-grain opacity-20" />
        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
          <div className="max-w-2xl mx-auto text-center">
            <div className="mx-auto h-20 w-20 rounded-full bg-accent grid place-items-center mb-6 shadow-lg">
              <CheckCircle2 className="h-10 w-10 text-accent-foreground" />
            </div>
            <div className="inline-flex items-center gap-2 rounded-full bg-accent/15 text-accent px-4 py-1.5 text-xs font-bold uppercase tracking-widest mb-6">
              <Sparkles className="h-3.5 w-3.5" />
              Förfrågan mottagen
            </div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-[0.95]">
              Tack för din <span className="text-accent">förfrågan!</span>
            </h1>
            <p className="mt-6 text-lg text-primary-foreground/85">
              Vi har tagit emot din intresseanmälan tillsammans med din önskelista.
              En av våra eventkonsulter går igenom dina val och återkommer
              personligen med pris och tillgänglighet.
            </p>
          </div>
        </div>
      </section>

      {/* NEXT STEPS */}
      <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-16">
        <div className="max-w-4xl mx-auto">
          <h2 className="font-display text-2xl sm:text-3xl font-bold text-center mb-10">
            Vad händer nu?
          </h2>
          <div className="grid gap-6 sm:grid-cols-3">
            {[
              {
                icon: CheckCircle2,
                title: "Mottagen",
                desc: "Vi har tagit emot din förfrågan och återkommer med pris och tillgänglighet.",
              },
              {
                icon: Clock,
                title: "Vi går igenom upplägget",
                desc: "Vårt team granskar era val och återkommer med pris, tillgänglighet och förslag.",
              },
              {
                icon: Phone,
                title: "Personlig dialog",
                desc: "Vi finslipar upplägget tillsammans tills allt sitter perfekt.",
              },
            ].map((step, i) => (
              <div
                key={i}
                className="rounded-2xl bg-card border border-border p-6 text-center"
              >
                <div className="mx-auto h-12 w-12 rounded-xl bg-accent/15 text-accent grid place-items-center mb-4">
                  <step.icon className="h-6 w-6" />
                </div>
                <div className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-2">
                  Steg {i + 1}
                </div>
                <h3 className="font-display text-lg font-bold">{step.title}</h3>
                <p className="mt-2 text-sm text-muted-foreground">{step.desc}</p>
              </div>
            ))}
          </div>

          {/* Contact strip */}
          <div className="mt-10 rounded-2xl bg-secondary/60 border border-border p-6 sm:p-8 text-center">
            <h3 className="font-display text-lg font-bold">Brådskande? Kontakta oss direkt</h3>
            <p className="mt-1 text-sm text-muted-foreground">Ring oss eller skicka ett mejl.</p>
            <div className="mt-5 flex flex-wrap gap-3 justify-center">
              <a
                href="tel:+46317975599"
                className="inline-flex items-center gap-2 rounded-full bg-card border border-border px-5 py-2.5 text-sm font-semibold hover:border-primary/30 transition"
              >
                <Phone className="h-4 w-4 text-accent" />
                031-797 55 99
              </a>
              <a
                href="mailto:info@eventsverige.se"
                className="inline-flex items-center gap-2 rounded-full bg-card border border-border px-5 py-2.5 text-sm font-semibold hover:border-primary/30 transition"
              >
                <Mail className="h-4 w-4 text-accent" />
                info@eventsverige.se
              </a>
            </div>
          </div>

          {/* CTA buttons */}
          <div className="mt-10 flex flex-wrap gap-3 justify-center">
            <Link
              to="/produkter"
              className="inline-flex items-center gap-2 rounded-full bg-accent text-accent-foreground px-6 py-3 font-bold hover:bg-accent/90 transition"
            >
              <ClipboardList className="h-4 w-4" />
              Bläddra i sortimentet
              <ArrowRight className="h-4 w-4" />
            </Link>
            <Link
              to="/"
              className="inline-flex items-center gap-2 rounded-full bg-secondary px-6 py-3 font-semibold hover:bg-secondary/70 transition"
            >
              <Home className="h-4 w-4" />
              Till startsidan
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
